import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as idb from 'utils/shared/idbUrl';

const STORAGE_KEY = 'latestPageUrl';
const INACTIVITY_THRESHOLD = 20 * 60 * 1000; // 20 minutes in milliseconds

const usePageInMemory = (key: string, saveState: () => void) => {
  const possibleModules = [
    'ilpackage',
    'flight',
    'hotel',
    'tour',
    'transfer',
    'dynamic_am',
    'dynamic_am_events',
    'dynamic_fmhm',
    'dynamic_fmhmsm',
    'hotel2',
  ];
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (key.includes('/dynamic') && !key?.toLowerCase()?.includes('token')) {
        return;
      }
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      if (user?.config?.base?.rememberModeEnabled) {
        saveState();
      }
      saveKeyForModule();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, pathname]);

  const saveKeyForModule = () => {
    let currentModule = possibleModules.find((md) => pathname?.toLowerCase().includes(`${md}`));
    const isHotel2 =
      pathname?.toLowerCase().includes('hotel') && pathname?.toLowerCase().includes('2');
    const isDynamic = search.toLocaleLowerCase().includes('matrixconfig=');
    const isDynamicEvents = search.toLocaleLowerCase().includes('searchoption=events');
    if (isDynamic) {
      currentModule = search.toLowerCase().split('matrixconfig=')[1].split('&')[0]?.toLowerCase();
    }
    if (isDynamicEvents) {
      currentModule = 'dynamic_am_events';
    }
    if (isHotel2) {
      currentModule = 'hotel2';
    }

    if (currentModule) {
      try {
        const storedData = localStorage.getItem(STORAGE_KEY);
        const data = storedData ? JSON.parse(storedData) : { lastmodify: Date.now() };

        data.lastmodify = Date.now();

        if (!data[currentModule]) {
          data[currentModule] = [];
        }

        if (key.includes('/result') || (isDynamic && key.includes('/details'))) {
          data[currentModule] = [key];
        } else if (!data[currentModule].includes(key)) {
          data[currentModule].push(key);
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (_) {}
    }
  };

  const onClickBack = (module: string, page: string) => {
    const storedData = localStorage.getItem(STORAGE_KEY);
    const data = storedData ? JSON.parse(storedData) : { lastmodify: Date.now() };
    if (data[module]) {
      const reversedIndex = data[module]
        .slice()
        .reverse()
        .findIndex((p: string) => p?.includes(page));
      const pageIndex = reversedIndex === -1 ? -1 : data[module].length - 1 - reversedIndex;
      if (pageIndex !== -1) {
        navigate(data[module][pageIndex]);
      }
    }
  };

  const onClickSearch = (module: string, backPath: string) => {
    const storedData = localStorage.getItem(STORAGE_KEY);
    const data = storedData ? JSON.parse(storedData) : { lastmodify: Date.now() };
    navigate(backPath);
    if (data[module]?.length) {
      setTimeout(() => {
        const newData = { ...data };
        newData[module].forEach((p: string) => {
          idb.deletePageState(p);
        });
        delete newData[module];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
      }, 300);
    }
  };

  return {
    onClickBack,
    onClickSearch,
  };
};

export const cleanupInactivePages = () => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) return;

    const data = JSON.parse(storedData);
    const currentTime = Date.now();
    const timeDiff = currentTime - (data.lastmodify || 0);

    if (timeDiff > INACTIVITY_THRESHOLD) {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {}
};

export default usePageInMemory;
